/* eslint-disable react/prop-types */
import React, { useState, useEffect, useContext } from 'react';
import { GeneralContext } from '../../../../core/app-context/GeneralContext';
import { ContentContainer } from './styles';
import Services from '../../services';
import DevolutionFormStep from './components/DevolutionFormStep';
import ItemsCreationStep from './components/ItemsCreationStep';
import FetchedOrderItems from './components/FetchedOrderItems';
import usePageStore from '../../store/pageStore';
import useConfigsStore from '../../store/configsStore';

export default function Content({ queryParametersData }) {
  const { setInfoMessage, setErrorMessage } = useContext(GeneralContext);

  /* Page configs store states ----------------------- */
  const storeId = useConfigsStore((state) => state.storeId);
  const devolutionPageConfigs = useConfigsStore(
    (state) => state.devolutionPageConfigs,
  );
  const storeConfigs = useConfigsStore((state) => state.storeConfigs);
  const integrationStatus = useConfigsStore((state) => state.integrationStatus);
  /* ------------------------------------------------- */

  /* Page store states ------------------------------- */
  const formData = usePageStore((state) => state.formData);
  const manualOrderItems = usePageStore((state) => state.manualOrderItems);
  const setFormData = usePageStore((state) => state.setFormData);
  const showClientCreationForm = usePageStore(
    (state) => state.showClientCreationForm,
  );
  const setShowClientCreationForm = usePageStore(
    (state) => state.setShowClientCreationForm,
  );
  const setOrderBeingCreated = usePageStore(
    (state) => state.setOrderBeingCreated,
  );
  const setVtexOrderItems = usePageStore((state) => state.setVtexOrderItems);
  const memphisOrderInfo = usePageStore((state) => state.memphisOrderInfo);
  const setMemphisOrderInfo = usePageStore(
    (state) => state.setMemphisOrderInfo,
  );
  const validVtexOrderItems = usePageStore(
    (state) => state.validVtexOrderItems,
  );
  // const isOrderBeingCreatedDirectly = usePageStore(
  //   (state) => state.isOrderBeingCreatedDirectly,
  // );
  // const setOrderBeingCreatedDirectly = usePageStore(
  //   (state) => state.setOrderBeingCreatedDirectly,
  // );
  /* ------------------------------------------------- */

  const [step, setStep] = useState(1);
  const [showAddressFields, setShowAddressFields] = useState(false);

  // This is responsible to show the address fields if the integration with Correios is enabled
  useEffect(() => {
    if (integrationStatus && integrationStatus.correios) {
      setShowAddressFields(true);
    }
  }, [integrationStatus]);

  /**
   * The steps are:
   * 1 - DevolutionForm (order data and client creation)
   * 2 - Items selection/insertion
   * 3 - Vtex item selection
   * 4 - Countdown to redirect to the return location selection page
   */
  const redirectToUrl = (url) => (window.location.href = url);
  const handleNextStep = (url) => {
    if (step === 1) {
      if (storeConfigs?.considerDevolutionItems) {
        if (storeConfigs?.enableVtexItemsSearch) {
          setStep(3);
        } else {
          setStep(2);
        }
      } else {
        // Just redirects to the return location selection page
        redirectToUrl(url);
      }
    } else if (step === 2 || step === 3) {
      // Just redirects to the return location selection page
      redirectToUrl(url);
    }
  };

  const handleSaveOrder = async ({ storeId, order }) => {
    const processedOrder = {
      externalOrderCode: order.orderNumber,
      clientId: order.clientId,
      commerce: order.commerce || 'N/A',
      oiOrderData:
        order.oiEmployeeName && order.oiEmployeeRegistrationNumber
          ? {
              oiEmployeeName: order.oiEmployeeName,
              oiEmployeeRegistrationNumber: order.oiEmployeeRegistrationNumber,
            }
          : null,
      invoiceKey:
        order.items && order.items.length > 0
          ? order.items[0].invoiceKey
          : null,
      items:
        order.items?.map((item) => ({
          name: item.itemName,
          price: parseFloat(item.price),
          quantity: parseInt(item.quantity),
          categoryId: item.category,
          reasonId: item.reason,
          resolution: item.resolution,
          imageUrl: item.imageUrl,
        })) ?? [],
    };
    const saveOrderResult = await Services.createOrder(storeId, processedOrder);
    if (!saveOrderResult) {
      setErrorMessage('Erro ao salvar o pedido');
      return null;
    } else {
      const { eboxFromQueryParam } = queryParametersData;
      // If the ebox code was provided as a query param, we set the method to CliqueRetire and set that ebox as the one selected
      if (eboxFromQueryParam) {
        const setReturnMethodResult = await Services.setOrderReturnMethod({
          orderId: saveOrderResult.orderId,
          returnMethodIndex: 1,
          boxCode: eboxFromQueryParam,
        });
        if (!setReturnMethodResult.success) {
          setErrorMessage(setReturnMethodResult.message);
          return null;
        }
        if (storeConfigs?.autoApproveDevolution) {
          const approveOrderResult = await Services.approveOrRejectOrder({
            orderId: saveOrderResult.orderId,
            choice: true,
          });
          if (!approveOrderResult) {
            setErrorMessage('Erro ao aprovar o pedido');
            return null;
          }
        }
      }
      return saveOrderResult;
    }
  };

  const handleOrderCreationFormSubmit = async (data) => {
    let clientId = null;
    setOrderBeingCreated(true);
    if (!showClientCreationForm) {
      const getClientIdResult = await Services.getClientId(data.email);
      if (!getClientIdResult) {
        setInfoMessage('Precisamos de mais algumas informações para continuar');
        setOrderBeingCreated(false);
        setShowClientCreationForm(true);
        return;
      } else {
        clientId = getClientIdResult;
      }
    } else {
      const createClientIdResult = await Services.createClient({
        name: data.name,
        email: data.email,
        phone: data.phone,
        document: showAddressFields ? data.cpf : null,
        address: showAddressFields
          ? {
              street: data.street,
              number: data.number,
              complement: data.complement,
              district: data.neighborhood,
              city: data.city,
              state: data.state,
              zipCode: data.cep,
              reference: '', // Not used yet
            }
          : null,
      });
      if (!createClientIdResult) {
        setInfoMessage(
          'Não foi possível salvar as informações, tente novamente mais tarde',
        );
        return;
      } else {
        clientId = createClientIdResult;
      }
    }
    const completeOrder = { ...data, clientId };
    if (!storeConfigs?.considerDevolutionItems) {
      const createdOrder = await handleSaveOrder({
        storeId,
        order: completeOrder,
      });
      setOrderBeingCreated(false);
      if (createdOrder) {
        handleNextStep(createdOrder.selectReturnLocationUrl);
      }
    } else {
      if (storeConfigs?.enableVtexItemsSearch) {
        const vtexOrderItems = await Services.getVtexOrderInfo(
          storeId,
          data.orderNumber,
        );
        if (!vtexOrderItems) {
          setErrorMessage(
            'Não foi possível ler o pedido VTEX, verifique as informações e tente novamente',
          );
          setOrderBeingCreated(false);
          return;
        } else {
          setVtexOrderItems(vtexOrderItems);
        }
      }
      // Store the order data to use on the order item creation step, if needed
      setFormData(completeOrder);
      setOrderBeingCreated(false);
      handleNextStep();
    }
  };

  const handleFormSubmitItemsCreation = async (data) => {
    setOrderBeingCreated(true);
    if (!storeConfigs.enableVtexItemsSearch) {
      const createdOrder = await handleSaveOrder({
        storeId,
        order: { ...formData, items: manualOrderItems },
      });
      setOrderBeingCreated(false);
      if (createdOrder) {
        handleNextStep(createdOrder.selectReturnLocationUrl);
      }
    } else {
      const createdOrder = await handleSaveOrder({
        storeId,
        order: {
          ...formData,
          items: validVtexOrderItems.map((item) => ({
            ...item,
            quantity: item.selectedQuantity,
            itemName: item.name,
            category: '7a8048bc-6545-4580-92c0-c62b22f4e872', //! refactor
          })),
        },
      });
      setOrderBeingCreated(false);
      if (createdOrder) {
        handleNextStep(createdOrder.selectReturnLocationUrl);
      }
    }
  };

  const handleGetMemphisOrderInfo = async (data) => {
    console.log(data);
    setOrderBeingCreated(true);
    if (!showClientCreationForm) {
      const memphisOrderInfo = await Services.getMemphisOrderInfo({
        storeId,
        memphisOrderId: data.orderNumber,
      });
      if (!memphisOrderInfo.success) {
        setOrderBeingCreated(false);
        setErrorMessage(
          memphisOrderInfo.message ||
            'Não foi possível ler o pedido, verifique as informações e tente novamente',
        );
        return;
      }
      setShowClientCreationForm(true);
      setMemphisOrderInfo(memphisOrderInfo.data);
      setOrderBeingCreated(false);
      return;
    } else {
      const saveOrderResult = await Services.createOrderWithClientInfo({
        storeId,
        order: {
          client: {
            name: data.name,
            email: data.email,
            phone: data.phone,
          },
          externalOrderCode: memphisOrderInfo?.eTicket,
          externalOrderId: memphisOrderInfo?.memphisOrderId,
          externalTrackingCode: memphisOrderInfo?.trackingCode,
          expirationDate: memphisOrderInfo?.expirationDate,
        },
      });
      if (!saveOrderResult) {
        setErrorMessage('Erro ao salvar o pedido. Tente novamente mais tarde.');
        setOrderBeingCreated(false);
        return null;
      }
      setOrderBeingCreated(false);
      handleNextStep(saveOrderResult.selectReturnLocationUrl);
    }
  };

  const handleVerifyOrderExistance = async (orderNumber) => {
    if (orderNumber) {
      setOrderBeingCreated(true);
      const link = await Services.getOrderLink({
        externalOrderCode: orderNumber,
        storeId,
      });
      setOrderBeingCreated(false);
      if (link) {
        redirectToUrl(link);
        return true;
      }
      return false;
    }
    return false;
  };

  const handleDevolutionFormStepButton = async (data) => {
    console.log(data);
    const orderExists = await handleVerifyOrderExistance(data.orderNumber);
    if (!orderExists) {
      if (storeConfigs?.enableMemphisOrderInfoSearch) {
        return await handleGetMemphisOrderInfo(data);
      } else {
        return await handleOrderCreationFormSubmit(data);
      }
    }
  };

  const disableUrlCompletedFields = !!queryParametersData?.orderNumber;

  return (
    <ContentContainer
      backgroundImage={devolutionPageConfigs?.mainImageUrl || null}
      backgroundSize={devolutionPageConfigs?.mainImageSizingMode || 'contain'}
      mobileBackgroundImage={
        devolutionPageConfigs?.mainImageMobileUrl ||
        devolutionPageConfigs?.mainImageUrl ||
        null
      }
      backgroundMobileSize={
        devolutionPageConfigs?.mainImageMobileSizingMode ||
        devolutionPageConfigs?.mainImageSizingMode ||
        'contain'
      }
      backgroundColor={
        devolutionPageConfigs?.backgroundColor || 'var(--color-blue-primary)'
      }
    >
      {step === 1 && (
        <DevolutionFormStep
          queryParametersData={queryParametersData}
          showAddressFields={showAddressFields}
          handleFormSubmit={handleDevolutionFormStepButton}
          hideOrderNumberField={!storeConfigs?.enableOrderNumberInput}
          hideEmailField={
            storeConfigs?.enableMemphisOrderInfoSearch &&
            !showClientCreationForm
          }
          disableUrlCompletedFields={disableUrlCompletedFields} // This prop will disable the fields that are already filled by the URL query parameters
        />
      )}
      {step === 2 && (
        <ItemsCreationStep handleFormSubmit={handleFormSubmitItemsCreation} />
      )}
      {step === 3 && (
        <FetchedOrderItems handleFormSubmit={handleFormSubmitItemsCreation} />
      )}
    </ContentContainer>
  );
}
